import { computed } from 'vue'
import { getMemberDuesStatusCore, DuesStatusType, type DuesStatus, type MemberLike } from '../shared/dues/status.js'

export { DuesStatusType } from '../shared/dues/status.js'
// Optional: expose core for advanced callers/tests without importing shared path directly
export { getMemberDuesStatusCore } from '../shared/dues/status.js'

/**
 * Composable to check if a member has to pay their dues for the current parade year
 * This centralized implementation ensures consistent behavior across the app
 *
 * @param memberId Optional member ID to check. If not provided, checks the current user
 * @returns Computed object with detailed dues status information
 */
export function useDuesStatus(memberId?: string) {
  const memberStore = useMembersStore()
  const currentUser = useCurrentUser()

  // Check payment status for the specified member or current user
  const duesStatus = computed(() => {
    // Determine which member to check
    let memberPath: string | null = null

    if (memberId) {
      // Check the specified member
      memberPath = `members/${memberId}`
    }
    else if (currentUser.value?.uid) {
      // Check the current user if no memberId provided
      memberPath = `members/${currentUser.value.uid}`
    }
    else {
      // If no member specified and no current user, return default unpaid status
      return {
        shouldPay: true,
        statusType: DuesStatusType.UNPAID,
        displayText: 'Not paid',
        tagValue: 'Unpaid',
        tagSeverity: 'danger' as const,
      }
    }

    const member = memberStore.members[memberPath]
    return getMemberDuesStatus(member)
  })

  return duesStatus
}

/**
 * Backward compatibility function that just returns the boolean value
 * Returns false until system config (parade year) is loaded to prevent wrong-year checks
 * @param memberId Optional member ID to check
 * @returns Computed boolean indicating if dues have to be paid
 */
export function useShouldPayDues(memberId?: string) {
  const duesStatus = useDuesStatus(memberId)
  const systemConfigStore = useSystemConfigStore()
  return computed(() => {
    // Don't show "pay dues" until config is loaded (prevents wrong year check)
    if (!systemConfigStore.loaded) return false
    return duesStatus.value.shouldPay
  })
}

/**
 * Utility function to get a member's dues status details
 * Provides rich information about payment status and display options
 *
 * @param member The member object to check
 * @returns Object with detailed dues status information
 */
export function getMemberDuesStatus(member: Member | null | undefined): DuesStatus {
  const { paradeYear } = useParadeYear()
  if (!member) {
    return getMemberDuesStatusCore(null, { paradeYear: paradeYear.value })
  }
  // Adapt Member to MemberLike shape (omit large / Vue specific fields)
  const adapted: MemberLike = {
    id: member.id,
    member_type: member.member_type as MemberLike['member_type'],
    lifetime: member.lifetime,
    purchases: member.purchases as unknown as MemberLike['purchases'],
    subkrewe: member.subkrewe
      ? {
        id: (member.subkrewe as { id?: string }).id,
        music: (member.subkrewe as { music?: string | null }).music,
        sponsored: (member.subkrewe as { sponsored?: boolean }).sponsored,
        approved: (member.subkrewe as { approved?: string | boolean | null }).approved,
      }
      : null,
  }
  return getMemberDuesStatusCore(adapted, { paradeYear: paradeYear.value })
}

/**
 * Utility function to check if a specific member has to pay dues
 * For backward compatibility
 *
 * @param member The member object to check
 * @returns Boolean indicating if the member has to pay dues
 */
export function memberShouldPayDues(member: Member | null | undefined): boolean {
  return getMemberDuesStatus(member).shouldPay
}
